import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Info, Tag } from "lucide-react";
import type { IbottaOffer } from "../types/Product";
import { useTranslation } from "../context/LanguageContext";
import { track } from "../utils/analytics";
import {
  formatIbottaDate,
  formatPrice,
  getIbottaBestAmount,
  getIbottaExpirationStatus,
} from "./ProductItem.utils";

interface IbottaOfferChipProps {
  offers: IbottaOffer[];
  /** Extra classes for the chip button (layout only). */
  className?: string;
}

/**
 * "Ibotta $X back" chip that opens a "Possible Ibotta offer" dialog.
 *
 * The match behind it is name-based and the offer data is a snapshot, so the
 * dialog always shows when the offer was seen, when it expires, and a caution
 * to confirm in the Ibotta app before buying.
 */
export default function IbottaOfferChip({ offers, className = "" }: IbottaOfferChipProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (offers.length === 0) return null;

  const best = getIbottaBestAmount(offers);
  const latestSeen = formatIbottaDate(Math.max(...offers.map((offer) => Number(offer.seenAt) || 0)) || undefined);
  const chipLabel = best > 0
    ? t("products.ibottaChip", { amount: formatPrice(best) })
    : t("products.ibottaChipGeneric");

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setOpen(true);
          track("ibotta_offer_viewed", {
            surface: "web_product_card",
            offerCount: offers.length,
            bestScore: Math.max(0, ...offers.map((offer) => Number(offer.score) || 0)),
          });
        }}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label={`${chipLabel}. ${t("products.ibottaChipAria")}`}
        aria-haspopup="dialog"
        className={`inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums text-pink-700 dark:text-pink-300 bg-pink-500/10 dark:bg-pink-900/40 px-1.5 py-0.5 rounded-md cursor-pointer hover:bg-pink-500/20 dark:hover:bg-pink-900/60 transition-colors ${className}`}
      >
        {chipLabel}
        <Info className="w-3 h-3" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(4px)" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onPointerDown={(event) => {
            event.stopPropagation();
            setOpen(false);
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white dark:bg-dark-800 rounded-xl p-5 max-w-sm w-full shadow-xl max-h-[85vh] flex flex-col"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 shrink-0 bg-pink-100 dark:bg-pink-900/40 rounded-full flex items-center justify-center">
                <Tag className="w-4 h-4 text-pink-600 dark:text-pink-300" />
              </div>
              <div className="min-w-0">
                <h3 id={titleId} className="text-base font-semibold leading-5 text-gray-900 dark:text-white">
                  {t("products.ibottaModalTitle")}
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-dark-400">
                  {latestSeen ? t("products.ibottaSubtitle", { date: latestSeen }) : t("products.ibottaSubtitleNoDate")}
                </p>
              </div>
            </div>

            <ul className="overflow-y-auto divide-y divide-gray-100 dark:divide-dark-700">
              {offers.map((offer) => {
                const status = getIbottaExpirationStatus(offer);
                const expirationDate = formatIbottaDate(offer.expiration);
                const expirationLabel = !expirationDate
                  ? status === "expired" ? t("products.ibottaExpired", { date: "" }).trim() : t("products.ibottaExpirationUnknown")
                  : status === "expired"
                    ? t("products.ibottaExpired", { date: expirationDate })
                    : status === "expiring_soon"
                      ? t("products.ibottaExpiresSoon", { date: expirationDate })
                      : t("products.ibottaExpires", { date: expirationDate });
                const expirationClass = status === "expired"
                  ? "text-red-700 dark:text-red-300"
                  : status === "expiring_soon"
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-gray-500 dark:text-dark-400";
                const amount = Number(offer.amount);
                const amountLabel = Number.isFinite(amount) && amount > 0
                  ? t("products.ibottaAmountBack", { amount: formatPrice(amount) })
                  : String(offer.currentValue || "").trim();
                const seenDate = formatIbottaDate(offer.seenAt);
                const details = String(offer.description || "").trim();
                const terms = String(offer.terms || "").trim();
                const loose = Number(offer.score) < 1;
                const expanded = expandedId === offer.offerId;
                const showExpiryCollapsed = status === "expired" || status === "expiring_soon";
                return (
                  <li key={offer.offerId}>
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : offer.offerId)}
                      aria-expanded={expanded}
                      aria-label={expanded
                        ? t("products.ibottaHideDetails", { name: offer.name })
                        : t("products.ibottaShowDetails", { name: offer.name })}
                      className="flex w-full items-start gap-2 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.03] rounded-lg -mx-1 px-1"
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`text-[13px] font-semibold leading-snug text-gray-900 dark:text-white ${expanded ? "" : "line-clamp-2"}`}>{offer.name}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-gray-500 dark:text-dark-400">
                          {details && <span>{details}</span>}
                          {showExpiryCollapsed && !expanded && <span className={`font-semibold ${expirationClass}`}>{expirationLabel}</span>}
                          {loose && (
                            <span className="rounded-full bg-gray-100 px-1.5 py-px text-[10px] font-semibold text-gray-600 dark:bg-dark-700 dark:text-dark-200">
                              {t("products.ibottaSimilar")}
                            </span>
                          )}
                        </p>
                      </div>
                      {amountLabel && (
                        <span className="shrink-0 text-[13px] font-extrabold tabular-nums text-pink-700 dark:text-pink-300">{amountLabel}</span>
                      )}
                      <ChevronDown className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                    {expanded && (
                      <div className="mb-2.5 space-y-1 rounded-lg bg-pink-50/70 px-3 py-2 dark:bg-pink-900/10">
                        <p className={`text-[11px] font-semibold ${expirationClass}`}>
                          {expirationLabel}
                          {seenDate && <span className="font-normal text-gray-500 dark:text-dark-400"> · {t("products.ibottaSeen", { date: seenDate })}</span>}
                        </p>
                        {loose && <p className="text-[11px] text-gray-500 dark:text-dark-400">{t("products.ibottaPartialMatch")}</p>}
                        {terms && <p className="text-[11px] leading-snug text-gray-600 dark:text-dark-300">{terms}</p>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <p className="mt-2 border-t border-gray-100 pt-2.5 text-[11px] leading-relaxed text-gray-500 dark:border-dark-700 dark:text-dark-400">{t("products.ibottaCaution")}</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-3 w-full py-2 bg-pink-600 text-white rounded-lg font-medium hover:bg-pink-700 transition-colors cursor-pointer"
              style={{ touchAction: "manipulation" }}
            >
              {t("products.gotIt")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
